<template>
  <q-card flat bordered>
    <q-card-section>
      <div class="row justify-between">
        <div class="col">
          <div class="text-h6">Email Notification Settings</div>
          <div>
            Get the latest market information sent to your email, with latest rents, concessions,
            and an Excel export attached
          </div>
        </div>
        <div class="q-my-auto col-3 flex justify-end">
          <q-toggle
            id="intercom-email-toggle"
            v-model="activated"
            dense
            color="primary"
            label="Activate"
            size="lg"
          />
        </div>
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div id="intercom-email-select">
        <email-select
          :emails="emails"
          :can-externally-share="canExternallyShare"
          @update-emails="updateEmails"
        />
      </div>
      <div class="row">
        <q-banner
          v-if="billedPerUnit && invalidEmailList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. External sharing is only available for properties in
                {{ organization?.display_name }}'s portfolio.
              </div>
            </div>
          </template>
        </q-banner>
        <q-banner
          v-else-if="billedPerSeat && invalidEmailList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. To add to your subscription, please reach out.
              </div>
            </div>
          </template>
          <template #action>
            <q-btn flat no-caps @click="openIntercom"> Upgrade </q-btn>
          </template>
        </q-banner>
      </div>
      <div id="intercom-email-cadence" class="q-mt-md">
        <div class="q-my-auto">Receive a morning email every:</div>
        <div class="row q-gutter-x-sm q-mt-sm">
          <q-checkbox
            :model-value="schedule"
            val="Monday"
            label="Monday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Tuesday"
            label="Tuesday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Wednesday"
            label="Wednesday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Thursday"
            label="Thursday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Friday"
            label="Friday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Saturday"
            label="Saturday"
            @update:model-value="updateSchedule"
          />
          <q-checkbox
            :model-value="schedule"
            val="Sunday"
            label="Sunday"
            @update:model-value="updateSchedule"
          />
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import EmailSelect from '@/components/EmailSelect.vue'
import type { EmailScheduleAttributes } from 'common'
import { cronExpressionToSchedule } from '@/utils'
import { useUserStore } from '@/stores/user.ts'
import { useStripeStore } from '@/stores/stripe'

declare const Intercom: (action: string, ...args: any[]) => void

export default {
  name: 'EmailNotifications',
  components: { EmailSelect },
  props: {
    emailSchedule: {
      type: Object as PropType<EmailScheduleAttributes>,
      default: undefined
    },
    canExternallyShare: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    reportIsInPortfolio: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    invalidEmailList: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update-emails', 'update-schedule'],
  data() {
    return {
      daysOfWeek: {
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
        Sunday: 7
      } as { [key: string]: number },
      schedule: [] as string[],
      emails: [] as string[]
    }
  },
  mounted() {
    if (this.emailSchedule) {
      this.schedule = cronExpressionToSchedule(this.emailSchedule.cron_expression)
      this.updateSchedule(this.schedule)
      this.emails = this.emailSchedule.emails
      this.updateEmails(this.emails)
    }
  },
  computed: {
    user() {
      return this.$auth0.user.value
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    },
    activated: {
      get() {
        return (this.emails ?? []).length > 0 && (this.schedule ?? []).length > 0
      },
      set(value: boolean) {
        if (value) {
          if (this.user?.email) {
            this.emails = [this.user.email]
            this.schedule = ['Monday']
          } else {
            throw new Error('User email not found')
          }
        } else {
          this.emails = []
          this.schedule = []
        }

        this.$emit('update-emails', this.emails)
        this.$emit('update-schedule', this.schedule)
      }
    },
    organization() {
      return useUserStore().organization
    }
  },
  methods: {
    updateEmails(emails: string[]) {
      this.emails = emails
      this.$emit('update-emails', emails)
    },
    updateSchedule(values: string[]) {
      values.sort((a, b) => {
        return this.daysOfWeek[a] - this.daysOfWeek[b]
      })
      this.schedule = values
      this.$emit('update-schedule', values)
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    }
  }
}
</script>
